import React, { useCallback } from "react";
import { useI18n } from "@core/i18n/i18n";
import { useObservable } from "micro-observables";
import { AnimatePresence } from "framer-motion";
import { useUser } from "@domain/user/hooks/use-user";
import { e2eTestId } from "@e2e/test-ids";
import { isNotNil } from "@utils/assert";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { templateManager } from "@core/service-container";
import { TemplateCard } from "@ui/components/program/program-card";
import { EmptyTemplates } from "@ui/pages/program/components/empty-templates";
import { EmptyTemplatesSearch } from "@ui/pages/program/components/empty-templates-search";
import { useTemplateFilters } from "@ui/pages/program/hooks/use-template-filters";
import { TemplateSortKey, useTemplateSort } from "@ui/pages/program/hooks/use-template-sort";
import { SearchFilter } from "@ui/pages/program/components/search-filter";
import { SortDropdown } from "@ui/pages/program/components/sort-dropdown";
import { useProgramCard } from "@ui/components/program/hooks/use-program-card";
import { EmptyContainer, Layout, TabContainer, TabHeader, CardWrapper } from "@ui/pages/program/components/tabs.styles";

export const TemplatesTab: React.FC = () => {
	const { format } = useI18n();
	const user = useUser();
	const { isDesktop } = useDimensions();
	const templates = useObservable(templateManager.templateItems) ?? [];
	const { lastTemplateAdded, navigateToTemplate, getTemplateCardActions } = useProgramCard();
	const { setSearch, filteredTemplates, templateTotalCount } = useTemplateFilters(templates);
	const { allSortOptions, setSortKey, sortedTemplates: sortedFilteredTemplates } = useTemplateSort(filteredTemplates);
	const filterTemplates = useCallback((search: string) => setSearch(search), [setSearch]);
	const sortTemplates = useCallback((key: TemplateSortKey) => setSortKey(key), [setSortKey]);

	return templateTotalCount === 0 ? (
		<EmptyContainer>
			<EmptyTemplates />
		</EmptyContainer>
	) : (
		<TabContainer>
			<TabHeader>
				<SearchFilter
					placeholder={format("templateListing.templates.searchInputPlaceholder")}
					onChange={filterTemplates}
				/>
				{isDesktop && <SortDropdown sortOptions={allSortOptions} onChange={sortTemplates} />}
			</TabHeader>
			{sortedFilteredTemplates.length === 0 ? (
				<EmptyContainer>
					<EmptyTemplatesSearch />
				</EmptyContainer>
			) : (
				<Layout data-test={e2eTestId.createTemplatePage.templateGrid}>
					<AnimatePresence presenceAffectsLayout>
						{isNotNil(user) &&
							sortedFilteredTemplates.map(template => (
								<CardWrapper key={template.id} layoutId={template.id}>
									<TemplateCard
										template={template}
										cardActions={getTemplateCardActions(template)}
										onClick={() => navigateToTemplate(template)}
										editor={user}
										isNew={lastTemplateAdded === template.id}
									/>
								</CardWrapper>
							))}
					</AnimatePresence>
				</Layout>
			)}
		</TabContainer>
	);
};
